import React, { useEffect, useState } from "react";
import { useLocation, RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import { Formik } from "formik";
import { Form, Input, Radio } from "formik-antd";
import { Page, setPage } from "features/appstate/AppStateSlice";
import {
  listClientSettings,
  addOrUpdateClientSettings,
  getLanguageSettingsListAll,
} from "./ClientSettingsActions";
import {
  ClientSettingsWithDetails,
  ClientSettingsAddForm,
  LanguageSettings,
} from "api/sms-api";
import qs from "qs";
import { Row, Col, Button, Spin } from "antd";
import { RootState } from "store/store";
import i18n from "i18n";
import {
  surveyTypeStore,
  textDiscriminator,
  LanguageSettingsCode,
  defaultClientSettingsValues,
  clientSettingLabelTextType,
  defaultEnglishClientSettingsValues,
} from "../../constants";
import RichEditor from "components/RichEditor";
import SaveButton from "components/SaveButton";
import { RadioChangeEvent } from "antd/lib/radio";

interface ClientSettingsTextProps {
  items: Array<ClientSettingsWithDetails>;
  loading: boolean;
  listClientSettings: (
    surveyType: string,
    discriminator: string,
    languageSettingsCode: string,
    isBlankMode?: boolean
  ) => void;
  addOrUpdateClientSettings: (params: Array<ClientSettingsAddForm>) => void;
  languageSettings: Array<LanguageSettings>;
  getLanguageSettingsListLoading: boolean;
  getLanguageSettingsListAll: () => void;
  setPage: (page: Page) => void;
}

const ClientSettingsText: React.FC<
  ClientSettingsTextProps & RouteComponentProps
> = (props) => {
  const location = useLocation();
  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [languageSettingMode, setLanguageSettingMode] = useState<string>(
    LanguageSettingsCode.ja
  );

  useEffect(() => {
    props.setPage(Page.textDisplaySettings);
  }, [props.setPage]);

  useEffect(() => {
    props.listClientSettings(
      (parsed.surveyType || surveyTypeStore) as string,
      textDiscriminator,
      languageSettingMode
    );
  }, [props.listClientSettings]);

  useEffect(() => {
    props.getLanguageSettingsListAll();
  }, [props.getLanguageSettingsListAll]);

  if (props.loading || props.getLanguageSettingsListLoading) {
    return <Spin />;
  }

  const handleSubmit = async (form: any) => {
    const keys: Array<string> = Object.keys(form).filter((key: string) =>
      props.items.find((item: ClientSettingsWithDetails) => item.code === key)
    );
    const forms: Array<ClientSettingsAddForm> = keys.map((key: string) => {
      const item = props.items.find(
        (item: ClientSettingsWithDetails) => item.code === key
      );
      const languageSetting = props.languageSettings.find(
        (ls) => ls.code === form.languageSetting
      );
      if (item) {
        return {
          settingsTypeId: item.settingsTypeId,
          languageSettingsId: languageSetting ? languageSetting.id : "",
          settingsValue: form[key],
          surveyType: (parsed.surveyType || surveyTypeStore) as string,
        };
      } else {
        return {
          settingsTypeId: "",
          languageSettingsId: languageSetting ? languageSetting.id : "",
          settingsValue: "",
          surveyType: (parsed.surveyType || surveyTypeStore) as string,
        };
      }
    });
    await props.addOrUpdateClientSettings(forms);
    await props.listClientSettings(
      (parsed.surveyType || surveyTypeStore) as string,
      textDiscriminator,
      form.languageSetting
    );
  };

  const handleReset = async () => {
    await props.listClientSettings(
      (parsed.surveyType || surveyTypeStore) as string,
      textDiscriminator,
      languageSettingMode,
      true
    );
  };

  const handleLanguageSettingChange = async (e: RadioChangeEvent) => {
    if (e.target.value) {
      await props.listClientSettings(
        (parsed.surveyType || surveyTypeStore) as string,
        textDiscriminator,
        e.target.value as string
      );
      setLanguageSettingMode(e.target.value as string);
    }
  };

  const initialValues: any = {
    languageSetting: languageSettingMode,
  };

  for (const item of props.items) {
    const defaultSettingsValues =
      languageSettingMode === LanguageSettingsCode.ja
        ? defaultClientSettingsValues
        : defaultEnglishClientSettingsValues;
    const defaultValue = defaultSettingsValues[item.code] || "";
    initialValues[item.code] =
      item?.clientSettings?.settingsValue ?? defaultValue;
    // Set the language
    const languageSettingsId = item?.clientSettings?.languageSettingsId ?? "";
    const languageSetting = props.languageSettings.find(
      (ls) => ls.id === languageSettingsId
    );
    initialValues.languageSetting = languageSetting
      ? languageSetting.code
      : languageSettingMode;
  }

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {(formikBag) => {
          const formItems = Object.keys(initialValues)
            .filter((key) => key !== "languageSetting")
            .map((key: string) => {
              const clientSetting = props.items.find(
                (item) => item.code === key
              );
              const itemName = clientSetting ? clientSetting.name : "";
              let editor = (
                <RichEditor
                  value={formikBag.values[key] || ""}
                  onChange={(value) => {
                    formikBag.setFieldValue(key, value);
                  }}
                />
              );
              if (clientSettingLabelTextType.includes(key)) {
                editor = <Input name={key} />;
              }
              const result = (
                <Row key={key} justify="start">
                  <Col span={18}>
                    <Form.Item
                      name={key}
                      label={itemName}
                      validateTrigger={["onBlur"]}
                      rules={[
                        {
                          validator(_, value) {
                            if (value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(i18n.t("validation.required"))
                            );
                          },
                        },
                      ]}
                    >
                      {editor}
                    </Form.Item>
                  </Col>
                </Row>
              );
              return result;
            });
          return (
            <Form layout="vertical">
              <Row justify="start">
                <Col span={18}>
                  <Form.Item name="languageSetting">
                    <Radio.Group
                      name="languageSetting"
                      className="form-radio-group"
                      defaultValue={initialValues.languageSetting}
                      onChange={handleLanguageSettingChange}
                    >
                      <Radio.Button
                        name={LanguageSettingsCode.ja}
                        value={LanguageSettingsCode.ja}
                      >
                        {i18n.t("clientSettings.japanese")}
                      </Radio.Button>
                      <Radio.Button
                        name={LanguageSettingsCode.en}
                        value={LanguageSettingsCode.en}
                      >
                        {i18n.t("clientSettings.english")}
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="start">
                <Col span={18}>
                  <Button
                    type="primary"
                    className="mail-exclusion-padding"
                    onClick={handleReset}
                  >
                    {i18n.t("clientSettings.resetToDefaults")}
                  </Button>
                </Col>
              </Row>
              {formItems}
              <Row justify="end" style={{ marginTop: "2.5em" }}>
                <SaveButton />
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    items: state.clientSettings.items,
    loading: state.clientSettings.listLoading,
    languageSettings: state.clientSettings.languageSettings,
    getLanguageSettingsListLoading:
      state.clientSettings.getLanguageSettingsListLoading,
  };
};

const mapDispatchToProps = {
  setPage,
  listClientSettings,
  addOrUpdateClientSettings,
  getLanguageSettingsListAll,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientSettingsText);
